import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

const SHIFTS = ["General", "A (Morning)", "B (Evening)", "C (Night)"];

function Field({ label, testid, value, onChange, className, ...rest }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} {...rest} />
    </div>
  );
}

export function CreateStaffDialog({ open, onOpenChange, form, setForm, roles, users, nextEmp, onSubmit }) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="create-staff-button"><Plus className="w-4 h-4 mr-1" /> Create Staff Account</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="create-staff-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Create Staff Account</DialogTitle><DialogDescription className="sr-only">Create a staff login account and assign its role</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name" testid="staff-name-input" value={form.name} onChange={set("name")} />
            <Field label="Employee ID" testid="staff-employee-id-input" value={form.employee_id} onChange={(v) => set("employee_id")(v.toUpperCase())} placeholder={nextEmp ? `Auto: ${nextEmp}` : "Auto"} className="font-mono" />
          </div>
          <Field label="Official Email" testid="staff-email-input" type="email" value={form.email} onChange={set("email")} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Password (optional if PIN set)" testid="staff-password-input" type="text" value={form.password} onChange={set("password")} placeholder="Min 6 chars" />
            <Field label="Secure PIN (4 digits)" testid="staff-pin-input" type="text" inputMode="numeric" maxLength={4} value={form.pin} onChange={(v) => set("pin")(v.replace(/\D/g, ""))} placeholder="Factory login" className="font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobile" testid="staff-mobile-input" value={form.mobile} onChange={set("mobile")} />
            <Field label="Designation" testid="staff-designation-input" value={form.designation} onChange={set("designation")} />
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.name} ({r.department})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Department" testid="staff-department-input" value={form.department} onChange={set("department")} />
            <div className="col-span-2">
              <Label>Reports to (manager — used for office escalation & team views)</Label>
              <Select value={form.manager_id || "none"} onValueChange={(v) => set("manager_id")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="staff-manager-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="Branch" testid="staff-branch-input" value={form.branch} onChange={set("branch")} placeholder="HO" />
            <Field label="Plant" testid="staff-plant-input" value={form.plant} onChange={set("plant")} placeholder="Plant-1" />
            <div>
              <Label>Shift</Label>
              <Select value={form.shift || "none"} onValueChange={(v) => set("shift")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="staff-shift-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="Joining Date" testid="staff-joining-input" type="date" value={form.joining_date} onChange={set("joining_date")} />
          </div>
          <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={onSubmit} data-testid="staff-create-submit">Create Account</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
